<script lang="ts">
  import type { Product } from '@agapi/mlc-core';
  import { TextField } from '@agapi/ui/text-field';
  import { Button } from '@agapi/ui/button';
  import { Loader } from '@agapi/ui/loader';
  import { Card, CardContent, CardHeader } from '@agapi/ui/card';
  import { stack, hstack, flex } from '@agapi/ui/panda/patterns';
  import * as m from '$lib/parachute/messages';
  import { css } from '@agapi/ui/panda/css';
  import { Bell, CheckCircle, X } from 'phosphor-svelte';
  import { page } from '$app/state';
  import { getLocaleFromUrl } from '../../../utils';
  import { createBackInStockSubscription } from '../../../services';
  import { backInStockFormStyles, cardStyles, submitButtonStyles } from './back-in-stock-form.styles';

  const {product}: {product: Product} = $props();

  const styles = backInStockFormStyles();

  let email = $state('');
  let subscribeLoading = $state(false);
  let subscribeSuccess = $state(false);
  let subscribeError = $state('');

  // Get current locale
  const locale = getLocaleFromUrl(page.url);

  const handleSubscribe = async (event?: Event) => {
    // Prevent default form submission if called from form submit
    event?.preventDefault();

    subscribeLoading = true;
    subscribeError = '';

    try {
      // Validate email format
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        throw new Error(m.notifyFormEmailError());
      }

      await createBackInStockSubscription(fetch, {
        email,
        productSku: product.variants[0].sku.toString(),
        language: locale.language
      });

      subscribeSuccess = true;
      email = '';
    } catch (error: any) {
      // Handle specific error codes from service
      if (error.message === 'subscription_failed') {
        subscribeError = m.notifyError();
      } else {
        // For validation errors or other user-facing errors, show them directly
        subscribeError = error.message || m.notifyError();
      }
    } finally {
      subscribeLoading = false;
    }
  };

  // Generate unique IDs for ARIA
  const formId = `back-in-stock-form-${product.variants[0].sku}`;
  const emailFieldId = `${formId}-email`;
  const errorId = `${formId}-error`;
  const statusId = `${formId}-status`;
</script>

<Card
  spacing="snug"
  colorVariant="secondary.subdued"
  css={cardStyles}
>
  {#if subscribeSuccess}
    <!-- Success State -->
    <CardContent>
      <div class={styles.successContainer}>
        <div class={styles.successIcon}>
          <CheckCircle weight="fill" size={64}/>
        </div>
        <div class={styles.successContent}>
          <h3 class={styles.successTitle}>
            {m.notifyButtonCompletedLabel()}
          </h3>
          <p class={styles.successMessage}>
            {m.notifyFormEmailSuccess()}
          </p>
        </div>
      </div>
    </CardContent>
  {:else}
    <!-- Default State -->
    <CardHeader>
      <div class={styles.headerContainer}>
        <Bell weight="fill" size={48}/>
        <div class={styles.headerContent}>
          <h3 class={styles.headerTitle}>
            {m.notifyFormTitle()}
          </h3>
          <p class={styles.headerDescription}>
            {m.outOfStockMessage()}
          </p>
        </div>
      </div>
    </CardHeader>

    <CardContent>
      <form
        id={formId}
        onsubmit={handleSubscribe}
        aria-labelledby="form-title"
        aria-describedby={subscribeError ? errorId : undefined}
        novalidate
      >
        <div class={styles.formContainer}>
          <!-- Hidden form title for screen readers -->
          <h3 id="form-title" class={styles.hiddenFormTitle}>
            {m.notifyFormTitle()}
          </h3>

          <div class={styles.emailFieldContainer}>
            <TextField
              label={m.notifyFormEmailLabel()}
              name="email"
              type="email"
              bind:value={email}
              placeholder={m.notifyFormEmailPlaceholder()}
              required
              hideLabel
              error={!!subscribeError}
              helperText={subscribeError || ''}
              rootStyles={css.raw({ width: '100%', my: 0 })}
              floatingLabel
              disabled={subscribeLoading}
            />
          </div>

          <!-- Error message for screen readers -->
          {#if subscribeError}
            <div
              id={errorId}
              role="alert"
              aria-live="polite"
              class={styles.hiddenErrorMessage}
            >
              {subscribeError}
            </div>
          {/if}

          <Button
            type="submit"
            disabled={subscribeLoading || !email.trim()}
            variant="cta"
            size="md"
            aria-describedby={statusId}
            css={submitButtonStyles}
          >
            {#if subscribeLoading}
              <Loader css={{ w: 4, h: 4 }} aria-hidden="true" />
              <span>{m.loading()}</span>
            {:else}
              <Bell weight="bold" size={18} aria-hidden="true"/>
              {m.notifyButtonLabel()}
            {/if}
          </Button>

          <!-- Status message for screen readers -->
          <div
            id={statusId}
            role="status"
            aria-live="polite"
            class={styles.hiddenStatusMessage}
          >
            {#if subscribeLoading}
              {m.loading()}
            {:else if subscribeSuccess}
              {m.notifyButtonCompletedLabel()}
            {/if}
          </div>
        </div>
      </form>
    </CardContent>
  {/if}
</Card>
